import type { ClientDeviceWithDevice } from '@maru44/huntre-utils/src/models/clientDevice'
import { CommunicationStatus, communicationStatus } from '@maru44/huntre-utils/src/models/communicationStatus'
import { IsTrappingStatus } from '@maru44/huntre-utils/src/models/device'
import type { Sim } from '@maru44/huntre-utils/src/models/sim'
import { Box, Grid, Paper, Stack, Typography } from '@mui/material'
import { flush } from '@sentry/react'
import { type ReactNode, useCallback, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { IconPopover } from 'src/components/atoms/IconPopover'
import { StatusChip } from 'src/components/atoms/StatusChip'
import { LastSeen } from 'src/components/device/LastSeen'
import { ClientDeviceSwitch } from 'src/components/dialogs/device/ClientDeviceSwitch'
import { useClientDevice } from 'src/hooks/clientDevice/useClientDevice'
import { formatTimestamp } from 'src/utils/firebase'
import { capture } from 'src/utils/sentry'
import type { KeyedMutator } from 'swr'
import { PopoverWhatIsTurnedOff } from '../../../components/device/PopoverIsTurnedOff'
import { Communication } from './Communication'

type ClientDeviceWithSim = ClientDeviceWithDevice & { sim: Sim | null }

type ClientDeviceDetailProps = {
  clientId: string
  data: ClientDeviceWithSim
  mutate: KeyedMutator<ClientDeviceWithSim>
}

// 重要な状態を上部にカードで大きく見せるためのラッパー
type StatusCardProps = {
  label: ReactNode
  children: ReactNode
}

const StatusCard = ({ label, children }: StatusCardProps) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
    </Box>
    <Box>{children}</Box>
  </Paper>
)

export const ClientDeviceDetail = ({ clientId, data, mutate }: ClientDeviceDetailProps) => {
  const { updateIsTurnedOff } = useClientDevice(data.device.id, clientId)

  const isTurnedOff = useMemo(() => {
    return data.clientDevice.isTurnedOff
  }, [data])

  const handleChangeIsTurnedOff = useCallback(async () => {
    const nextIsTurnedOff = !isTurnedOff
    try {
      await updateIsTurnedOff(nextIsTurnedOff)
    } catch (e) {
      capture(e)
      await flush(100)
      toast.error('通知の変更に失敗しました')
      return
    }
    toast.success(`通知を${nextIsTurnedOff ? 'off' : 'on'}にしました`)
    await mutate()
  }, [isTurnedOff, clientId, mutate, updateIsTurnedOff])

  const trappingStatus = useMemo((): IsTrappingStatus => {
    if (data.device.isTrapping) return IsTrappingStatus.Trapping
    return IsTrappingStatus.NotTrapping
  }, [data.device.isTrapping])

  // 通信可(正常)なら通信状況の行は出さず、注意が必要な状態のときだけ表示する
  const commStatus = useMemo(() => communicationStatus(data.device, data.sim), [data.device, data.sim])
  const showCommunicationRow = !!commStatus && commStatus !== CommunicationStatus.Ok

  const offNotice = (
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
      }}
    >
      通知オフ
    </Typography>
  )

  const cards = useMemo((): { key: string; label: ReactNode; body: ReactNode }[] => {
    return [
      {
        key: 'trapping',
        label: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            罠状況
            <IconPopover icon="?">
              <Typography>磁石が外れていると作動、ついていると未作動となります</Typography>
            </IconPopover>
          </Box>
        ),
        body: !isTurnedOff ? (
          <Stack spacing={0.5}>
            <Box>
              <StatusChip status={trappingStatus} />
            </Box>
            {data.device.isTrapping && !!data.device.trappedAt && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                捕獲日時: {formatTimestamp(data.device.trappedAt)}
              </Typography>
            )}
          </Stack>
        ) : (
          offNotice
        ),
      },
      {
        key: 'lastSeen',
        label: '最終通信',
        body: <LastSeen timestamp={data.device.lastMessageReceivedAt} />,
      },
      {
        key: 'abnormal',
        label: '異常検知',
        body: !isTurnedOff ? <StatusChip status={data.device.operationStatus} /> : offNotice,
      },
      ...(showCommunicationRow
        ? [
            {
              key: 'communication',
              label: '通信状況',
              body: <Communication sim={data.sim} data={data} />,
            },
          ]
        : []),
    ]
  }, [data, isTurnedOff, trappingStatus, showCommunicationRow])

  // カード枚数に応じて幅を調整(4の倍数なら4列、それ以外は3列)
  const cardMd = cards.length % 4 === 0 ? 3 : 4

  return (
    <Stack
      spacing={3}
      sx={{
        mt: 2,
      }}
    >
      {/* 状態カード */}
      <Grid container spacing={2}>
        {cards.map((c) => (
          <Grid
            key={c.key}
            size={{
              xs: 12,
              sm: 6,
              md: cardMd,
            }}
          >
            <StatusCard label={c.label}>{c.body}</StatusCard>
          </Grid>
        ))}
      </Grid>

      {/* 通知設定 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography variant="subtitle2">通知</Typography>
              <PopoverWhatIsTurnedOff />
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {isTurnedOff ? '罠の作動・異常を通知しません' : '罠の作動・異常をメールでお知らせします'}
            </Typography>
          </Box>
          <ClientDeviceSwitch clientDevice={data.clientDevice} onUpdate={handleChangeIsTurnedOff} showStatusLabel />
        </Box>
      </Paper>
    </Stack>
  )
}
